import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Gift, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { local, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";

const EMPTY = { code: "", title: "", trial_days: 90, valid_until: "", active: true };

// Superadmin management of event codes that extend a new organisation's free trial.
export function SATrialPromos() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const { data: promos = [] } = useQuery({ queryKey: ["trial-promos"], queryFn: async () => (await local.get("/trial-promos")).data });
  const refresh = () => qc.invalidateQueries({ queryKey: ["trial-promos"] });
  const save = useMutation({
    mutationFn: async (p) => (await local.post("/trial-promos", { ...p, trial_days: Number(p.trial_days), valid_until: p.valid_until || null })).data,
    onSuccess: () => { toast.success("Trial code saved"); refresh(); setOpen(false); setForm(EMPTY); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const del = useMutation({
    mutationFn: async (code) => (await local.delete(`/trial-promos/${code}`)).data,
    onSuccess: () => { toast.success("Trial code deleted"); refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="mt-8" data-testid="sa-trial-promos">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-display text-lg font-semibold text-white"><Gift className="h-5 w-5 text-amber-400" /> Extended-trial event codes</h2>
        <Button data-testid="sa-add-trial-promo" size="sm" className="gap-1.5 bg-amber-600 hover:bg-amber-700" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> New trial code</Button>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {promos.map((p) => (
          <div key={p.code} data-testid={`sa-trial-promo-${p.code}`} className="rounded-xl border border-amber-400/20 bg-amber-400/[0.04] p-5">
            <p className="font-mono text-sm font-bold text-amber-400">{p.code}</p>
            <p className="font-display text-base font-semibold text-white">{p.title}</p>
            <p className="mt-2 text-sm text-slate-300"><b className="text-white">{p.trial_days}</b>-day free trial {p.valid_until && <span className="text-slate-500">· code valid until {fmtDate(p.valid_until)}</span>}</p>
            <p className="mt-1 text-xs text-slate-400" data-testid={`sa-trial-promo-count-${p.code}`}>{p.redemptions} organisation{p.redemptions === 1 ? "" : "s"} signed up</p>
            <p className="mt-1 break-all font-mono text-[11px] text-teal-400">/signup?promo={p.code}</p>
            <div className="mt-4 flex items-center justify-between border-t border-white/10 pt-3">
              <div className="flex items-center gap-2 text-xs text-slate-400"><Switch data-testid={`sa-trial-promo-toggle-${p.code}`} checked={!!p.active} onCheckedChange={() => save.mutate({ ...p, active: !p.active })} /> {p.active ? "Active" : "Inactive"}</div>
              <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" data-testid={`sa-trial-promo-delete-${p.code}`} onClick={() => window.confirm(`Delete trial code ${p.code}? New sign-ups will no longer get the extended trial.`) && del.mutate(p.code)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">New extended-trial code</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Code</Label><Input data-testid="trial-promo-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="NMA2026" /></div>
              <div className="space-y-1.5"><Label>Trial days</Label><Input data-testid="trial-promo-days" type="number" min={1} max={365} value={form.trial_days} onChange={(e) => setForm({ ...form, trial_days: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5"><Label>Title</Label><Input data-testid="trial-promo-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Conference offer" /></div>
            <div className="space-y-1.5"><Label>Code valid until (optional)</Label><Input data-testid="trial-promo-valid" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="trial-promo-save" disabled={!form.code || save.isPending} className="bg-amber-600 hover:bg-amber-700" onClick={() => save.mutate(form)}>{save.isPending ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
